import type { QualityPreset } from '../config/GameConfig';
import type { LevelDefinition } from '../levels/LevelTypes';
import type { LeaderboardEntry, LeaderboardResult } from '../systems/LeaderboardSystem';
import type { LevelRecord, SaveData } from '../systems/SaveSystem';
import type { RoundStats } from '../systems/Progression';

export interface UIActions {
  play: () => void;
  continueGame: () => void;
  chooseLevel: (level: number) => void;
  resume: () => void;
  restart: () => void;
  nextLevel: () => void;
  title: () => void;
  togglePause: () => void;
  leaderboard: () => void;
  playerNameChanged: (name: string) => string;
  settingsChanged: (settings: SaveData['settings']) => void;
}

export interface HUDData {
  level: number;
  levelName: string;
  defeated: number;
  total: number;
  elapsed: number;
  detected: number;
  combo: number;
}

export class GameUI {
  readonly joystick: HTMLElement;
  readonly joystickKnob: HTMLElement;
  readonly kickButton: HTMLButtonElement;
  readonly mobilePause: HTMLButtonElement;
  private readonly titleScreen: HTMLElement;
  private readonly hud: HTMLElement;
  private readonly modal: HTMLElement;
  private readonly modalPanel: HTMLElement;
  private readonly kickPrompt: HTMLElement;
  private readonly tutorial: HTMLElement;
  private readonly danger: HTMLElement;
  private readonly debugPanel: HTMLElement;
  private readonly mobileControls: HTMLElement;
  private readonly continueButton: HTMLButtonElement;
  private readonly playerNameInput: HTMLInputElement;
  private settings: SaveData['settings'];
  private playerName = 'Adventurer';
  private lastHUD = '';
  private returnScreen: 'title' | 'pause' = 'title';
  private levels: LevelDefinition[] = [];
  private save?: SaveData;

  constructor(private readonly root: HTMLElement, private readonly actions: UIActions, settings: SaveData['settings']) {
    this.settings = settings;
    root.innerHTML = `
      <section id="title-screen" class="screen title-screen" aria-label="Mouthbound Ruins main menu">
        <div class="title-art"></div><div class="title-shade"></div>
        <div class="title-copy"><div class="eyebrow">A FLANKING ADVENTURE</div><h1>Mouthbound<br><span>Ruins</span></h1>
          <p>Big mouths. Tiny blind spots. One mighty boot.</p>
          <label class="player-name-entry"><span>ADVENTURER NAME</span><input id="player-name" type="text" maxlength="18" autocomplete="nickname" spellcheck="false" aria-label="Adventurer name"></label>
          <div class="menu-buttons"><button class="primary" data-action="play">Begin adventure</button><button data-action="continue">Continue</button>
            <button data-action="levels">Choose chamber</button><button data-action="instructions">How to play</button><button data-action="settings">Settings</button><button class="scores-button" data-action="scores">Top scorers</button></div>
        </div><div class="title-footer">WASD to move · drag to look · SPACE to kick</div>
      </section>
      <section id="hud" class="hud hidden" aria-live="polite">
        <div class="hud-top"><div class="level-pill"><span id="hud-level">CHAMBER 1</span><strong id="hud-level-name">Sunwake Courtyard</strong></div>
          <div class="stats-strip"><div><span>MONSTERS</span><strong id="hud-monsters">0 / 12</strong></div><div><span>TIME</span><strong id="hud-time">0:00</strong></div><div><span>SPOTTED</span><strong id="hud-detected">0</strong></div></div>
          <button id="pause-button" class="round-button" aria-label="Pause">Ⅱ</button></div>
        <div id="combo-badge" class="combo-badge hidden">KICK CHAIN ×<b>2</b></div>
      </section>
      <div id="kick-prompt" class="kick-prompt hidden"><small>SAFE ANGLE</small><strong>KICK!</strong><span><kbd>SPACE</kbd> or <kbd>E</kbd></span></div>
      <div id="tutorial-card" class="tutorial-card hidden"><span class="tutorial-step">RUIN RULE</span><strong></strong><p></p></div>
      <div id="danger-vignette" class="danger-vignette"></div>
      <div id="capture-mouth" class="capture-mouth"><div></div><div></div></div>
      <div id="mobile-controls" class="mobile-controls hidden">
        <div id="joystick" class="joystick" aria-label="Movement joystick"><div class="joystick-arrows">▲</div><div id="joystick-knob" class="joystick-knob"></div></div>
        <button id="mobile-kick" class="mobile-kick" aria-label="Kick"><span>⚡</span>KICK</button><button id="mobile-pause" class="mobile-pause" aria-label="Pause">Ⅱ</button>
      </div>
      <section id="modal" class="screen modal hidden" role="dialog" aria-modal="true"><div id="modal-panel" class="modal-panel"></div></section>
      <aside id="debug-panel" class="debug-panel hidden"></aside>`;

    this.titleScreen = this.required('#title-screen'); this.hud = this.required('#hud'); this.modal = this.required('#modal');
    this.modalPanel = this.required('#modal-panel'); this.kickPrompt = this.required('#kick-prompt'); this.tutorial = this.required('#tutorial-card');
    this.danger = this.required('#danger-vignette'); this.debugPanel = this.required('#debug-panel');
    this.mobileControls = this.required('#mobile-controls');
    this.joystick = this.required('#joystick'); this.joystickKnob = this.required('#joystick-knob');
    this.kickButton = this.required<HTMLButtonElement>('#mobile-kick'); this.mobilePause = this.required<HTMLButtonElement>('#mobile-pause');
    this.continueButton = this.required<HTMLButtonElement>('[data-action="continue"]');
    this.playerNameInput = this.required<HTMLInputElement>('#player-name');
    this.playerNameInput.addEventListener('change', () => this.commitPlayerName(this.playerNameInput.value));
    this.playerNameInput.addEventListener('keydown', (event) => { if (event.key === 'Enter') this.playerNameInput.blur(); });
    this.required('[data-action="play"]').addEventListener('click', () => this.click(actions.play));
    this.continueButton.addEventListener('click', () => this.click(actions.continueGame));
    this.required('[data-action="levels"]').addEventListener('click', () => {
      if (this.save) this.showLevelSelect(this.levels, this.save.unlockedLevel, this.save.completedLevels);
    });
    this.required('[data-action="instructions"]').addEventListener('click', () => this.showInstructions());
    this.required('[data-action="settings"]').addEventListener('click', () => this.showSettings('title'));
    this.required('[data-action="scores"]').addEventListener('click', actions.leaderboard);
    this.required('#pause-button').addEventListener('click', actions.togglePause);
  }

  configureProgress(levels: LevelDefinition[], save: SaveData): void {
    this.levels = levels;
    this.save = save;
    this.playerName = save.playerName;
    this.playerNameInput.value = this.playerName;
    this.continueButton.classList.toggle('hidden', save.unlockedLevel <= 1 && !save.completedLevels[1]);
  }

  showTitle(levels: LevelDefinition[], save: SaveData): void {
    this.hideGameplayOverlays(); this.modal.classList.add('hidden'); this.modal.classList.remove('settings-modal', 'leaderboard-modal'); this.titleScreen.classList.remove('hidden'); this.hud.classList.add('hidden'); this.mobileControls.classList.add('hidden');
    this.configureProgress(levels, save);
  }

  showHUD(): void { this.titleScreen.classList.add('hidden'); this.modal.classList.add('hidden'); this.modal.classList.remove('settings-modal', 'leaderboard-modal'); this.hud.classList.remove('hidden'); this.mobileControls.classList.remove('hidden'); }

  updateHUD(data: HUDData): void {
    const key = JSON.stringify({ ...data, elapsed: Math.floor(data.elapsed) }); if (key === this.lastHUD) return; this.lastHUD = key;
    this.setText('#hud-level', `CHAMBER ${data.level}`); this.setText('#hud-level-name', data.levelName);
    this.setText('#hud-monsters', `${data.defeated} / ${data.total}`); this.setText('#hud-time', this.time(data.elapsed)); this.setText('#hud-detected', String(data.detected));
    const combo = this.required('#combo-badge'); combo.classList.toggle('hidden', data.combo < 2); combo.querySelector('b')!.textContent = String(data.combo);
  }

  showKickPrompt(show: boolean): void { this.kickPrompt.classList.toggle('hidden', !show); this.kickButton.classList.toggle('ready', show); }
  showDanger(show: boolean): void { this.danger.classList.toggle('active', show); }
  showDebug(show: boolean, html = ''): void { this.debugPanel.classList.toggle('hidden', !show); if (show) this.debugPanel.innerHTML = html; }

  showTutorial(step: 0 | 1 | 2 | 3): void {
    if (step === 0) { this.tutorial.classList.add('hidden'); return; }
    const content: [string, string, string] = {
      1: ['1 / 3', 'Stay away from their mouths.', 'The glowing front is the danger zone.'],
      2: ['2 / 3', 'Get behind them.', 'Pillars help you slip around unseen.'],
      3: ['3 / 3', 'KICK!', 'The green ring means your angle is safe.'],
    }[step] as [string, string, string];
    this.tutorial.querySelector('.tutorial-step')!.textContent = content[0];
    this.tutorial.querySelector('strong')!.textContent = content[1]; this.tutorial.querySelector('p')!.textContent = content[2];
    this.tutorial.classList.remove('hidden');
  }

  playCapture(): void { this.root.classList.add('caught'); window.setTimeout(() => this.root.classList.remove('caught'), 850); }

  showPause(): void {
    this.returnScreen = 'pause'; this.modalPanel.innerHTML = `<div class="modal-icon">☀</div><div class="eyebrow">ADVENTURE PAUSED</div><h2>Catch your breath.</h2>
      <p>The ruins will wait.</p><div class="modal-buttons"><button class="primary" data-modal="resume">Keep exploring</button><button data-modal="restart">Restart chamber</button><button data-modal="settings">Settings</button><button data-modal="title">Main menu</button></div>`;
    this.bindModal(); this.openModal();
  }

  showGameOver(level: number): void {
    this.modalPanel.innerHTML = `<div class="caught-title">CAUGHT!</div><h2>That mouth was facing you.</h2><p>No adventurers were eaten. The monster just won this round.</p>
      <div class="tip-card"><b>RUIN TIP</b> Break line of sight, wait for it to turn, then circle wide.</div><div class="modal-buttons"><button class="primary" data-modal="restart">Try chamber ${level} again</button><button data-modal="title">Main menu</button></div>`;
    this.bindModal(); this.openModal();
  }

  showComplete(level: number, stats: RoundStats, stars: number, score: number, final: boolean): void {
    const starMarkup = [1, 2, 3].map((n) => `<span class="star ${n <= stars ? 'earned' : ''}">★</span>`).join('');
    this.modalPanel.innerHTML = `<div class="eyebrow">CHAMBER ${level} COMPLETE</div><h2>${final ? 'The temple is smiling!' : 'Ruins cleared!'}</h2><div class="stars">${starMarkup}</div>
      <div class="result-grid"><div><span>${final ? 'FINAL SCORE' : 'SCORE'}</span><b>${score.toLocaleString()}</b></div><div><span>TIME</span><b>${this.time(stats.elapsedSeconds)}</b></div><div><span>SPOTTED</span><b>${stats.detected}</b></div><div><span>BEST CHAIN</span><b>×${Math.max(1, stats.bestCombo)}</b></div></div>
      <div class="modal-buttons">${final ? '<button class="primary" data-modal="title">Final score · Main menu</button>' : '<button class="primary" data-modal="next">Enter next chamber</button>'}<button data-modal="restart">Play again</button></div>`;
    this.bindModal(); this.openModal();
  }

  showLevelSelect(levels: LevelDefinition[], unlocked: number, records: Record<number, LevelRecord> = {}): void {
    if (!levels.length) return;
    this.modalPanel.innerHTML = `<div class="eyebrow">CHOOSE A CHAMBER</div><h2>Where to next?</h2><div class="level-grid">${levels.map((level) => {
      const open = level.id <= unlocked; const stars = records[level.id]?.stars ?? 0;
      return `<button class="level-card ${open ? '' : 'locked'}" data-level="${level.id}" ${open ? '' : 'disabled'}><span>${open ? String(level.id).padStart(2, '0') : '◆'}</span><b>${level.name}</b><small>${open ? `${'★'.repeat(stars)}${'☆'.repeat(3 - stars)}` : 'Locked'}</small></button>`;
    }).join('')}</div><button data-modal="close">Back</button>`;
    this.modalPanel.querySelectorAll<HTMLElement>('[data-level]').forEach((button) => button.addEventListener('click', () => this.click(() => this.actions.chooseLevel(Number(button.dataset.level)))));
    this.bindModal(); this.openModal();
  }

  showLeaderboardLoading(): void {
    this.returnScreen = 'title';
    this.titleScreen.classList.add('hidden');
    this.modalPanel.innerHTML = `<div class="eyebrow">HALL OF ADVENTURERS</div><h2>Top scorers</h2><div class="leaderboard-loading" aria-live="polite">Finding the bravest ruin explorers…</div><button data-modal="close">Back</button>`;
    this.bindModal();
    this.openModal('leaderboard');
  }

  showLeaderboard(result: LeaderboardResult, currentPlayerId: string): void {
    if (this.modal.classList.contains('hidden') || !this.modal.classList.contains('leaderboard-modal')) return;
    const rows = result.entries.map((entry, index) => this.leaderboardRow(entry, index, currentPlayerId)).join('');
    this.modalPanel.innerHTML = `<div class="eyebrow">HALL OF ADVENTURERS</div><h2>Top scorers</h2>
      <div class="leaderboard-head"><span>RANK</span><span>ADVENTURER</span><span>PROGRESS</span><span>SCORE</span></div>
      <div class="leaderboard-list">${rows || '<div class="leaderboard-empty">No scores yet. Clear a chamber to take first place!</div>'}</div>
      <p class="leaderboard-note">${result.shared ? 'Shared scores from everyone playing Mouthbound Ruins.' : 'Offline: showing scores saved on this device.'}</p>
      <button class="primary" data-modal="close">Back to adventure</button>`;
    this.bindModal();
  }

  private showInstructions(): void {
    this.modalPanel.innerHTML = `<div class="eyebrow">HOW TO PLAY</div><h2>Outsmart the mouths.</h2><div class="instructions-grid">
      <div class="instruction"><i>WASD</i><div><b>Move</b><p>Use Shift to sprint. Sprinting is louder.</p></div></div><div class="instruction danger-rule"><i>▲</i><div><b>Front = danger</b><p>A glowing mouth can catch you only when its snap connects.</p></div></div>
      <div class="instruction safe-rule"><i>↶</i><div><b>Side or back = safe</b><p>Circle around while the monster slowly turns.</p></div></div><div class="instruction"><i>␣</i><div><b>Kick</b><p>Press Space or E when KICK appears. One good kick wins.</p></div></div>
      <div class="instruction"><i>◉</i><div><b>Look</b><p>Drag the mouse or right side of the touchscreen.</p></div></div><div class="instruction"><i>🎮</i><div><b>Gamepad ready</b><p>Move with left stick, look with right, kick with X/A.</p></div></div></div>
      <button class="primary" data-modal="close">Got it</button>`; this.bindModal(); this.openModal();
  }

  private showSettings(returnScreen: 'title' | 'pause'): void {
    this.returnScreen = returnScreen;
    if (returnScreen === 'title') this.titleScreen.classList.add('hidden');
    this.modalPanel.innerHTML = `<div class="eyebrow">SETTINGS</div><h2>Make the ruins yours.</h2><div class="settings-list">
      <label><span>Adventurer name</span><input id="settings-player-name" class="name-input" type="text" maxlength="18" autocomplete="nickname" spellcheck="false"></label>
      <label><span>Music <output id="music-value">${Math.round(this.settings.musicVolume * 100)}%</output></span><input id="music-volume" type="range" min="0" max="1" step="0.01" value="${this.settings.musicVolume}"></label>
      <label><span>Effects <output id="effects-value">${Math.round(this.settings.effectsVolume * 100)}%</output></span><input id="effects-volume" type="range" min="0" max="1" step="0.01" value="${this.settings.effectsVolume}"></label>
      <label class="toggle-row"><span>Mute all sound</span><input id="mute-audio" type="checkbox" ${this.settings.muted ? 'checked' : ''}></label>
      <label><span>Graphics quality</span><select id="quality">${(['LOW', 'MEDIUM', 'HIGH'] as QualityPreset[]).map((q) => `<option ${q === this.settings.quality ? 'selected' : ''}>${q}</option>`).join('')}</select></label></div>
      <button class="primary" data-modal="save-settings">Save settings</button>`;
    this.modalPanel.querySelector<HTMLInputElement>('#settings-player-name')!.value = this.playerName;
    const music = this.modalPanel.querySelector<HTMLInputElement>('#music-volume')!; const effects = this.modalPanel.querySelector<HTMLInputElement>('#effects-volume')!;
    music.addEventListener('input', () => { this.modalPanel.querySelector('#music-value')!.textContent = `${Math.round(Number(music.value) * 100)}%`; });
    effects.addEventListener('input', () => { this.modalPanel.querySelector('#effects-value')!.textContent = `${Math.round(Number(effects.value) * 100)}%`; });
    this.bindModal(); this.openModal('settings');
  }

  private bindModal(): void {
    this.modalPanel.querySelector('[data-modal="resume"]')?.addEventListener('click', () => this.click(this.actions.resume));
    this.modalPanel.querySelector('[data-modal="restart"]')?.addEventListener('click', () => this.click(this.actions.restart));
    this.modalPanel.querySelector('[data-modal="next"]')?.addEventListener('click', () => this.click(this.actions.nextLevel));
    this.modalPanel.querySelector('[data-modal="title"]')?.addEventListener('click', () => this.click(this.actions.title));
    this.modalPanel.querySelector('[data-modal="settings"]')?.addEventListener('click', () => this.showSettings('pause'));
    this.modalPanel.querySelector('[data-modal="close"]')?.addEventListener('click', () => this.closeModal());
    this.modalPanel.querySelector('[data-modal="save-settings"]')?.addEventListener('click', () => {
      const nameInput = this.modalPanel.querySelector<HTMLInputElement>('#settings-player-name');
      if (nameInput) this.commitPlayerName(nameInput.value);
      this.settings = {
        musicVolume: Number(this.modalPanel.querySelector<HTMLInputElement>('#music-volume')!.value),
        effectsVolume: Number(this.modalPanel.querySelector<HTMLInputElement>('#effects-volume')!.value),
        muted: this.modalPanel.querySelector<HTMLInputElement>('#mute-audio')!.checked,
        quality: this.modalPanel.querySelector<HTMLSelectElement>('#quality')!.value as QualityPreset,
      };
      this.actions.settingsChanged(this.settings); this.closeModal();
    });
  }

  private openModal(kind: 'standard' | 'settings' | 'leaderboard' = 'standard'): void {
    this.modal.classList.toggle('settings-modal', kind === 'settings');
    this.modal.classList.toggle('leaderboard-modal', kind === 'leaderboard');
    this.modal.classList.remove('hidden');
  }

  private closeModal(): void {
    if (this.returnScreen === 'pause') this.showPause();
    else {
      this.modal.classList.add('hidden');
      this.modal.classList.remove('settings-modal', 'leaderboard-modal');
      this.titleScreen.classList.remove('hidden');
    }
  }
  private hideGameplayOverlays(): void { this.showKickPrompt(false); this.showTutorial(0); this.showDanger(false); this.showDebug(false); }
  private click(action: () => void): void { action(); }
  private commitPlayerName(value: string): void {
    this.playerName = this.actions.playerNameChanged(value);
    this.playerNameInput.value = this.playerName;
  }
  private leaderboardRow(entry: LeaderboardEntry, index: number, currentPlayerId: string): string {
    const rank = index + 1;
    return `<div class="leaderboard-row rank-${Math.min(rank, 4)} ${entry.playerId === currentPlayerId ? 'current' : ''}"><b>${rank}</b><span><strong>${this.escape(entry.name)}</strong>${entry.playerId === currentPlayerId ? '<small>YOU</small>' : ''}</span><span>Chamber ${entry.level}<small>${entry.stars} ★</small></span><strong>${entry.score.toLocaleString()}</strong></div>`;
  }
  private escape(value: string): string { return value.replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]!); }
  private time(seconds: number): string { const value = Math.max(0, Math.floor(seconds)); return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`; }
  private setText(selector: string, value: string): void { this.required(selector).textContent = value; }
  private required<T extends HTMLElement = HTMLElement>(selector: string): T { const element = this.root.querySelector<T>(selector); if (!element) throw new Error(`Missing UI element ${selector}`); return element; }
}
